import { html } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { useReduxState } from "Shared/redux/useReduxState";
import { SmartBenefitsSubsection, SmartBenefitsWidget, URLsField, ValueField } from "./KenticoKontent";

export const useBenefits = () => {
	const [kontent] = useReduxState("kontent.smartBenefits.smartBenefitsTest.contents");

	const smartProgramCardTemplate = (widget: SmartBenefitsWidget) => html`
		<div class="grid grid-cols-1 grid-rows-3 rounded-xl border border-solid border-[#E0E0E0] bg-white shadow-md">
			<div
				class="h-full w-full bg-cover bg-center"
				style=${`background-image: url(${(widget.icon as URLsField).urls[0]})`}
			></div>
			<div class="h-full w-full py-3">
				<div class="text-base font-bold text-n-blue">${unsafeHTML(widget.title.value)}</div>
				<div class="text-xs text-[#444444]">${unsafeHTML(widget.text.value)}</div>
			</div>
			<div class="flex h-full w-full items-center justify-center gap-x-2 border-t border-solid border-[#E0E0E0]">
				<span
					class="cursor-pointer text-lg/none font-medium text-n-cyan hover:text-n-blue"
					@click=${() => (window.location.href = widget.url.value)}
				>
					${widget.linktext.value} <i class="jsh-chevron-right1 ml-2 text-base"></i>
				</span>
			</div>
		</div>
	`;

	const otherBenefitsCardTemplate = (widget: SmartBenefitsWidget) => html`
		<div class="h-full w-full rounded-xl border border-solid border-[#E0E0E0] bg-white p-2 shadow-sm">
			<div class="flex gap-2 pb-2">
				<div
					class="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0F3F7] text-base/none text-n-blue"
				>
					${unsafeHTML((widget.icon as ValueField).value)}
				</div>
				<div class="text-left">
					<div class="mb-1 font-bold text-n-blue">${unsafeHTML(widget.title.value)}</div>
					<div class="text-xs text-[#444444]">${unsafeHTML(widget.text.value)}</div>
				</div>
			</div>
			<div class="flex items-center justify-center gap-2 border-t border-solid border-[#E0E0E0] pt-2">
				<span
					class="cursor-pointer text-lg font-medium text-n-cyan hover:text-n-blue"
					@click=${() => (window.location.href = widget.url.value)}
				>
					${widget.linktext.value} <i class="jsh-chevron-right1 ml-2 text-base"></i>
				</span>
			</div>
		</div>
	`;

	const subTitleTemplate = (title: string) => html`
		<div class="mb-4 w-full text-left text-lg font-medium text-n-blue">${unsafeHTML(title)}</div>
	`;

	const aaWidgetTemplate = (widget: SmartBenefitsWidget) => html`
		<div
			class="relative row-span-2 flex items-center justify-center overflow-hidden rounded-xl bg-n-blue text-white"
		>
			<div class="relative flex h-full w-full flex-col items-center justify-center text-white">
				<img class="h-auto w-[90%]" src=${(widget.icon as URLsField).urls[0]} />
				<div class="mb-8 mt-3 font-body text-base/5">${unsafeHTML(widget.title.value)}</div>
				<div class="mb-4 font-body text-lg/5 font-black">${unsafeHTML(widget.text.value)}</div>
				<a
					class="flex items-center font-body text-lg/none text-white hover:text-n-cyan"
					href=${widget.url.value}
				>
					${unsafeHTML(widget.linktext.value)} <i class="jsh-chevron-right1 ml-2 text-base"></i>
				</a>
			</div>
		</div>
	`;

	const programsSubsectionTemplate = (subsection: SmartBenefitsSubsection) => {
		const aaWidgets = [kontent.widget[0]];
		const programWidgets = [kontent.widget[1], kontent.widget[2], kontent.widget[3], kontent.widget[4]];

		return html`
			<div>
				${subTitleTemplate(subsection.title.value)}
				<div
					class="grid h-[330px] grid-rows-2 gap-4"
					style=${`grid-template-columns: ${2 * aaWidgets.length}fr repeat(${
						programWidgets.length / 2
					}, 1fr)`}
				>
					${aaWidgets.map(aaWidgetTemplate)} ${programWidgets.map(smartProgramCardTemplate)}
				</div>
			</div>
		`;
	};

	const othersSubsectionTemplate = (subsection: SmartBenefitsSubsection) => {
		const widgets = [kontent.widget[5], kontent.widget[6], kontent.widget[7]];

		return html`
			<div>
				${subTitleTemplate(subsection.title.value)}
				<div
					class="grid h-[330px] grid-rows-3 gap-4"
					style=${`grid-template-columns: repeat(${widgets.length / 3}, minmax(0, 1fr))`}
				>
					${widgets.map(otherBenefitsCardTemplate)}
				</div>
			</div>
		`;
	};

	const subsectionTemplate = (subsection: SmartBenefitsSubsection, index: number) =>
		index === 0 ? programsSubsectionTemplate(subsection) : othersSubsectionTemplate(subsection);

	const htmlTemplate = () =>
		kontent
			? html`
					<div class="container">
						<div class="my-6 w-full text-left font-emphasis text-xl text-n-blue">
							${unsafeHTML(kontent.sectionTitle.value)}
						</div>
						<div class="grid w-full grid-cols-[3fr_1fr] gap-6">
							${kontent.subsection.map(subsectionTemplate)}
						</div>
					</div>
			  `
			: "";

	return { htmlTemplate };
};
